import api from "./api";
import type {
  Project,
  ProjectMember,
  TaskGroup,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectFilters,
  ProjectSLAStats,
  ProjectSLAExemptionRequest,
} from "@/types/project";
import type {
  Task,
  TaskComment,
  TaskChecklist,
  TaskTag,
  TaskWorkLog,
  TaskAttachment,
  TaskCreateRequest,
  TaskUpdateRequest,
  TaskMoveRequest,
  TaskStatusHistory,
  TaskFilters,
  WorkLogCreateRequest,
  WorkLogUpdateRequest,
  EmployeeWorkload,
} from "@/types/task";
import type { User } from "@/types/user";

export interface ProjectPurgeCounts {
  tasks: number;
  open_tasks: number;
  task_comments: number;
  task_attachments: number;
  task_status_logs: number;
  files: number;
  posts: number;
  schedules: number;
  chat_rooms: number;
  chat_messages: number;
  performance_distributions: number;
  kpi_value_events: number;
  linked_task_orders: number;
  members: number;
}

export interface ProjectPurgePreview {
  project: { id: number; name: string; status: string; project_type: string; created_at: string | null };
  counts: ProjectPurgeCounts;
  storage_objects: number;
  blockers: string[];
  can_purge: boolean;
}

export interface ProjectPurgeResult {
  project: ProjectPurgePreview["project"];
  counts: ProjectPurgeCounts;
  storage_objects: number;
  deleted_objects: number;
  shared_objects_kept: number;
  failed_objects: number;
}


export interface ProjectDetailResponse extends Project {
  members: (ProjectMember & { user?: User })[];
  task_groups: TaskGroup[];
  task_count: number;
  completed_task_count: number;
}

// Task assignee with user details for API response
export interface TaskAssigneeWithUser {
  id: number;
  task_id: number;
  user_id: string;
  assigned_at: string;
  user?: User;
}

// Task detail response with full relationships
export interface TaskDetailResponse extends Task {
  description?: string;
  estimated_hours?: number;
  comments: (TaskComment & { user?: User })[];
  work_logs: (TaskWorkLog & { user?: User })[];
  attachments: TaskAttachment[];
}

export interface BoardColumn {
  id: number;
  name: string;
  color?: string;
  order_num: number;
  tasks: Task[];
}

export const projectService = {
  // Projects
  async getProjects(params?: ProjectFilters): Promise<Project[]> {
    const response = await api.get<Project[]>("/projects", { params });
    return response.data;
  },

  async getProject(projectId: number): Promise<ProjectDetailResponse> {
    const response = await api.get<ProjectDetailResponse>(`/projects/${projectId}`);
    return response.data;
  },

  async createProject(data: ProjectCreateRequest): Promise<Project> {
    const response = await api.post<Project>("/projects", data);
    return response.data;
  },

  async updateProject(projectId: number, data: ProjectUpdateRequest): Promise<Project> {
    const response = await api.put<Project>(`/projects/${projectId}`, data);
    return response.data;
  },

  async deleteProject(projectId: number): Promise<void> {
    await api.delete(`/projects/${projectId}`);
  },

  /** 보관 프로젝트 영구 삭제 미리보기 (admin·super_admin) */
  async getPurgePreview(projectId: number): Promise<ProjectPurgePreview> {
    const res = await api.get<ProjectPurgePreview>(`/projects/${projectId}/purge-preview`);
    return res.data;
  },

  /** 보관 프로젝트 영구 삭제 (admin·super_admin, 되돌릴 수 없음) */
  async purgeProject(projectId: number): Promise<ProjectPurgeResult> {
    const res = await api.delete<ProjectPurgeResult>(`/projects/${projectId}/permanent`);
    return res.data;
  },

  async reorderProjects(projectIds: number[]): Promise<void> {
    await api.put("/projects/reorder", { project_ids: projectIds });
  },

  // Project Members
  async addMember(projectId: number, userId: string, role: string): Promise<ProjectMember> {
    const response = await api.post<ProjectMember>(`/projects/${projectId}/members`, {
      user_id: userId,
      role,
    });
    return response.data;
  },

  async updateMemberRole(projectId: number, memberId: number, role: string): Promise<ProjectMember> {
    const response = await api.put<ProjectMember>(`/projects/${projectId}/members/${memberId}`, {
      role,
    });
    return response.data;
  },

  async removeMember(projectId: number, userId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/members/${userId}`);
  },

  async transferOwnership(projectId: number, newOwnerId: string): Promise<void> {
    await api.put(`/projects/${projectId}/transfer-ownership`, {
      new_owner_id: newOwnerId,
    });
  },

  // Task Groups (Columns)
  async createTaskGroup(projectId: number, data: { name: string; color?: string }): Promise<TaskGroup> {
    const response = await api.post<TaskGroup>(`/projects/${projectId}/groups`, data);
    return response.data;
  },

  async updateTaskGroup(
    projectId: number,
    groupId: number,
    data: { name?: string; color?: string; order_num?: number }
  ): Promise<TaskGroup> {
    const response = await api.put<TaskGroup>(`/projects/${projectId}/groups/${groupId}`, data);
    return response.data;
  },

  async deleteTaskGroup(projectId: number, groupId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/groups/${groupId}`);
  },

  // Board View (Tasks grouped by columns)
  async getBoard(projectId: number): Promise<BoardColumn[]> {
    const response = await api.get<BoardColumn[]>(`/projects/${projectId}/board`);
    return response.data;
  },

  // Tasks
  async getTasks(projectId: number, params?: TaskFilters & { all?: boolean }): Promise<Task[]> {
    const response = await api.get<Task[]>(`/projects/${projectId}/tasks`, { params });
    return response.data;
  },

  async getTask(projectId: number, taskId: number): Promise<TaskDetailResponse> {
    const response = await api.get<TaskDetailResponse>(`/projects/${projectId}/tasks/${taskId}`);
    return response.data;
  },

  async createTask(projectId: number, data: TaskCreateRequest): Promise<Task> {
    const response = await api.post<Task>(`/projects/${projectId}/tasks`, data);
    return response.data;
  },

  async updateTask(projectId: number, taskId: number, data: TaskUpdateRequest): Promise<Task> {
    const response = await api.put<Task>(`/projects/${projectId}/tasks/${taskId}`, data);
    return response.data;
  },

  async deleteTask(projectId: number, taskId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}`);
  },

  async moveTask(projectId: number, taskId: number, data: TaskMoveRequest): Promise<Task> {
    const response = await api.put<Task>(`/projects/${projectId}/tasks/${taskId}/move`, data);
    return response.data;
  },

  async getTaskStatusHistory(projectId: number, taskId: number): Promise<TaskStatusHistory> {
    const response = await api.get<TaskStatusHistory>(
      `/projects/${projectId}/tasks/${taskId}/status-logs`
    );
    return response.data;
  },

  // Task Assignees
  async addTaskAssignee(projectId: number, taskId: number, userId: string): Promise<void> {
    await api.post(`/projects/${projectId}/tasks/${taskId}/assignees`, { user_id: userId });
  },

  async removeTaskAssignee(projectId: number, taskId: number, userId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}/assignees/${userId}`);
  },

  // Task Participants
  async addTaskParticipant(projectId: number, taskId: number, userId: string, role: string): Promise<void> {
    await api.post(`/projects/${projectId}/tasks/${taskId}/participants`, { user_id: userId, role });
  },

  async removeTaskParticipant(projectId: number, taskId: number, participantId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}/participants/${participantId}`);
  },

  // Task Checklists
  async addChecklist(
    projectId: number,
    taskId: number,
    content: string
  ): Promise<TaskChecklist> {
    const response = await api.post<TaskChecklist>(
      `/projects/${projectId}/tasks/${taskId}/checklists`,
      { content }
    );
    return response.data;
  },

  async updateChecklist(
    projectId: number,
    taskId: number,
    checklistId: number,
    data: { content?: string; is_completed?: boolean }
  ): Promise<TaskChecklist> {
    const response = await api.put<TaskChecklist>(
      `/projects/${projectId}/tasks/${taskId}/checklists/${checklistId}`,
      data
    );
    return response.data;
  },

  async deleteChecklist(projectId: number, taskId: number, checklistId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}/checklists/${checklistId}`);
  },

  // Task Comments
  async addTaskComment(projectId: number, taskId: number, content: string): Promise<TaskComment> {
    const response = await api.post<TaskComment>(
      `/projects/${projectId}/tasks/${taskId}/comments`,
      { content }
    );
    return response.data;
  },

  async deleteTaskComment(projectId: number, taskId: number, commentId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`);
  },

  // Like/unlike task comment
  async likeTaskComment(projectId: number, taskId: number, commentId: number): Promise<{ success: boolean; count: number }> {
    const response = await api.post<{ success: boolean; count: number }>(
      `/projects/${projectId}/tasks/${taskId}/comments/${commentId}/like`
    );
    return response.data;
  },

  async unlikeTaskComment(projectId: number, taskId: number, commentId: number): Promise<{ success: boolean; count: number }> {
    const response = await api.delete<{ success: boolean; count: number }>(
      `/projects/${projectId}/tasks/${taskId}/comments/${commentId}/like`
    );
    return response.data;
  },

  // Comment Attachments
  async uploadCommentAttachment(
    projectId: number,
    taskId: number,
    commentId: number,
    file: File
  ): Promise<{
    id: number;
    comment_id: number;
    file_name: string;
    file_path: string;
    file_size: number;
    file_type: string;
    created_at: string;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `/projects/${projectId}/tasks/${taskId}/comments/${commentId}/attachments`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  // Task Attachments
  async uploadAttachment(projectId: number, taskId: number, file: File): Promise<{
    id: number;
    task_id: number;
    file_name: string;
    file_path: string;
    file_size: number;
    file_type: string;
    created_at: string;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `/projects/${projectId}/tasks/${taskId}/attachments`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  async downloadAttachment(projectId: number, taskId: number, attachmentId: number): Promise<Blob> {
    const response = await api.get(
      `/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}/download`,
      { responseType: "blob" }
    );
    return response.data;
  },

  async deleteAttachment(projectId: number, taskId: number, attachmentId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`);
  },

  // Tags
  async getTags(projectId: number): Promise<TaskTag[]> {
    const response = await api.get<TaskTag[]>(`/projects/${projectId}/tags`);
    return response.data;
  },

  async createTag(projectId: number, data: { name: string; color: string }): Promise<TaskTag> {
    const response = await api.post<TaskTag>(`/projects/${projectId}/tags`, data);
    return response.data;
  },

  async deleteTag(projectId: number, tagId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/tags/${tagId}`);
  },

  async addTagToTask(projectId: number, taskId: number, tagId: number): Promise<void> {
    await api.post(`/projects/${projectId}/tasks/${taskId}/tags/${tagId}`);
  },

  async removeTagFromTask(projectId: number, taskId: number, tagId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}/tags/${tagId}`);
  },

  // Work Logs
  async getWorkLogs(projectId: number, taskId: number): Promise<TaskWorkLog[]> {
    const response = await api.get<TaskWorkLog[]>(
      `/projects/${projectId}/tasks/${taskId}/work-logs`
    );
    return response.data;
  },

  async addWorkLog(
    projectId: number,
    taskId: number,
    data: WorkLogCreateRequest
  ): Promise<TaskWorkLog> {
    const response = await api.post<TaskWorkLog>(
      `/projects/${projectId}/tasks/${taskId}/work-logs`,
      data
    );
    return response.data;
  },

  async updateWorkLog(
    projectId: number,
    taskId: number,
    workLogId: number,
    data: WorkLogUpdateRequest
  ): Promise<TaskWorkLog> {
    const response = await api.put<TaskWorkLog>(
      `/projects/${projectId}/tasks/${taskId}/work-logs/${workLogId}`,
      data
    );
    return response.data;
  },

  async deleteWorkLog(projectId: number, taskId: number, workLogId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}/work-logs/${workLogId}`);
  },

  // My Tasks (across all projects)
  async getMyTasks(params?: { status?: string; page?: number; page_size?: number }): Promise<Task[]> {
    const response = await api.get<Task[]>("/tasks/my", { params });
    return response.data;
  },

  // Employee Workload (간트 차트용)
  async getEmployeeWorkload(params?: {
    user_id?: string;
    exclude_completed?: boolean;
  }): Promise<EmployeeWorkload[]> {
    const response = await api.get<EmployeeWorkload[]>("/tasks/employee-workload", {
      params: {
        exclude_completed: true,
        ...params,
      },
    });
    return response.data;
  },

  // Statistics
  async getProjectStats(projectId: number): Promise<{
    total_tasks: number;
    completed_tasks: number;
    in_progress_tasks: number;
    todo_tasks: number;
    overdue_tasks: number;
  }> {
    const response = await api.get(`/projects/${projectId}/stats`);
    return response.data;
  },

  // SLA 면책 요청 목록 (승인 권한자용) — 대기 중만
  async getSLAExemptionRequests(): Promise<Task[]> {
    const response = await api.get<Task[]>("/tasks/sla-exemption-requests");
    return response.data;
  },

  // SLA 면책 요청 내역 (대기 + 승인 처리분 포함) — status: undefined(전체) | "pending" | "approved"
  async getSLAExemptions(status?: "pending" | "approved"): Promise<Task[]> {
    const response = await api.get<Task[]>("/tasks/sla-exemptions", {
      params: status ? { status } : undefined,
    });
    return response.data;
  },

  // 프로젝트별 SLA 대시보드 통계
  async getProjectSLAStats(projectId: number): Promise<ProjectSLAStats> {
    const response = await api.get<ProjectSLAStats>(
      `/projects/${projectId}/sla-stats`
    );
    return response.data;
  },

  // 프로젝트별 SLA 면책 대기 큐
  async getProjectSLAExemptionRequests(
    projectId: number
  ): Promise<ProjectSLAExemptionRequest[]> {
    const response = await api.get<ProjectSLAExemptionRequest[]>(
      `/projects/${projectId}/sla-exemption-requests`
    );
    return response.data;
  },

  // SLA 면책 관리
  async requestSLAExemption(projectId: number, taskId: number, data: { reason: string }): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/projects/${projectId}/tasks/${taskId}/sla/exemption-request`, data);
    return response.data;
  },

  async approveSLAExemption(projectId: number, taskId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/projects/${projectId}/tasks/${taskId}/sla/exemption-approve`);
    return response.data;
  },

  async rejectSLAExemption(projectId: number, taskId: number, data?: { reason?: string }): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/projects/${projectId}/tasks/${taskId}/sla/exemption-reject`, data || {});
    return response.data;
  },
};

export default projectService;
